import type {
  EnumPaymentType,
  CashierTransactionTimelineStep,
  EnumVerificationType,
} from "@/api/cashier";
import clsx from "clsx";
import { useStatusIcon } from "./useStatusIcon";
import styles from "./TransactionTimeline.module.css";
import { useFormatDate } from "@/hooks/useFormatDate";
import { TransactionStepTypeLabel } from "./TransactionStepTypeLabel";
import { useTranslation } from "react-i18next";
import { useTimer } from "@/hooks/useTimer";
import { formatSecondsToMMSS } from "@/utils/timerUtils";

export type PendingConfirmationStatus = {
  verificationType: EnumVerificationType;
  initialCountdownSeconds: number;
};

/* The TransactionTimelineStep component
  This component represents a single step in the transaction timeline.
  @param step - The transaction timeline step to display.
  @param transactionType - The type of transaction (e.g., withdrawal, deposit, transfer, cashAdjustment).
  @param onCompleteNow - Optional callback invoked when the customer clicks "Complete now".
  @param pendingConfirmationStatus - Optional pending confirmation data from WithdrawalRequestStatus.
  @returns A ReactElement representing the transaction timeline step.
*/
export const TransactionTimelineStep = ({
  step,
  transactionType,
  onCompleteNow,
  pendingConfirmationStatus,
}: {
  step: CashierTransactionTimelineStep;
  transactionType: EnumPaymentType;
  onCompleteNow?: () => void;
  pendingConfirmationStatus?: PendingConfirmationStatus;
}) => {
  const { t } = useTranslation();
  const { formatDate } = useFormatDate();

  const isPendingConfirmation =
    step.step_type === "request_submitted" &&
    step.step_status === "in_progress" &&
    !!pendingConfirmationStatus;

  const { secondsLeft } = useTimer(
    isPendingConfirmation
      ? pendingConfirmationStatus.initialCountdownSeconds
      : undefined,
    isPendingConfirmation
  );

  const icon = useStatusIcon(
    step.step_status === "in_progress" &&
      step.step_type === "request_submitted" &&
      transactionType === "withdrawal"
      ? "completed"
      : step.step_status
  );
  const stepStatusIndicatorClass =
    step.step_status === "completed" && styles.completed;

  const showCompleteNow =
    isPendingConfirmation &&
    secondsLeft > 0 &&
    pendingConfirmationStatus.verificationType === "otp" &&
    !!onCompleteNow;

  return (
    <div className={styles.step}>
      {icon && (
        <div className={clsx(styles.stepStatusIcon, styles[step.step_status])}>
          <img src={icon} alt={`${step.step_status}_icon`} />
          <div
            className={clsx(
              styles.stepStatusIndicator,
              stepStatusIndicatorClass
            )}
          />
        </div>
      )}
      <div className={styles.stepDetails}>
        <div className={styles.stepLabelRow}>
          <TransactionStepTypeLabel
            stepType={step.step_type}
            stepStatus={step.step_status}
            paymentType={transactionType}
            estimatedCompletion={step.estimated_completion}
            isPendingConfirmation={isPendingConfirmation}
            countdownTime={
              isPendingConfirmation
                ? formatSecondsToMMSS(secondsLeft)
                : undefined
            }
            verificationType={
              isPendingConfirmation
                ? pendingConfirmationStatus.verificationType
                : undefined
            }
          />
          {showCompleteNow && (
            <button
              type="button"
              className={styles.completeNowButton}
              onClick={onCompleteNow}
            >
              {t("transactions.history.timeline.steps.complete_now")}
            </button>
          )}
        </div>
        <time>{formatDate(step.timestamp)}</time>
      </div>
    </div>
  );
};
